/* eslint-disable */
import { forwardRef, useRef } from 'react'
import {
    Dialog,
    DialogActions,
    Box,
    Toolbar,
    Typography,
    Slide,
    Grow,
    IconButton,
    Breakpoint,
    Button,
    Paper,
} from '@mui/material'

import { TransitionProps } from '@mui/material/transitions'
import {
    Close as CloseIcon,
    Minimize as MinimizeIcon,
} from '@mui/icons-material'
import { Grid } from '.'
import Draggable from 'react-draggable'

interface UniversalModalProps {
    children?: React.ReactNode
    data?: any[]
    open?: boolean
    onClose?: any
    onMinimize?: any
    onOpen?: any
    ariaLabel?: string
    ariaDescription?: string
    title?: React.ReactNode
    description?: string
    fullScreen?: boolean
    wrapperStyle?: any
    formTitle?: string
    sx?: any
    maxWidth?: Breakpoint
    draggable?: boolean
    minimizable?: boolean
    timeStamp?: string
    buttons?: any
    headerActions?: React.ReactNode
}

// Full-screen modals slide up like a sheet; windowed modals scale in (the
// modern centered-dialog feel).
const SlideTransition = forwardRef(function SlideTransition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />
})
const GrowTransition = forwardRef(function GrowTransition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>
) {
    return <Grow ref={ref} {...props} />
})

const DraggableModal = (props: any) => {
    const nodeRef = useRef(null)
    return (
        <Draggable
            handle={`#draggable-dialog-title`}
            cancel={'[class*="MuiDialogContent-root"]'}
            nodeRef={nodeRef}
        >
            <Paper ref={nodeRef} {...props} />
        </Draggable>
    )
}

const UniversalModal: React.FC<UniversalModalProps> = ({
    open = false,
    onClose = () => {},
    onMinimize = () => {},
    title = '',
    timeStamp = '',
    fullScreen = false,
    children,
    wrapperStyle,
    sx = {},
    maxWidth = 'md',
    draggable = true,
    minimizable = false,
    buttons = [],
    headerActions,
}) => {
    const windowed = !fullScreen
    return (
        <Dialog
            fullScreen={fullScreen}
            fullWidth={windowed}
            // Windowed modals span ~80% of the viewport; the breakpoint cap is
            // disabled so the explicit 80vw width on the Paper wins.
            maxWidth={windowed ? false : maxWidth}
            open={open}
            onClose={(_event: any, reason: any) => {
                // Protect unsaved form input: a backdrop click doesn't discard
                // the modal (minimizes it if that's enabled). Esc / the X / the
                // action buttons close it explicitly.
                if (reason === 'backdropClick') {
                    if (minimizable) onMinimize()
                    return
                }
                onClose()
            }}
            TransitionComponent={windowed ? GrowTransition : SlideTransition}
            // Faded + blurred backdrop behind the window.
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(10,10,12,0.55)',
                        backdropFilter: 'blur(5px)',
                    },
                },
            }}
            PaperComponent={
                draggable && windowed ? DraggableModal : undefined
            }
            PaperProps={{
                elevation: windowed ? 24 : 0,
                sx: {
                    ...(windowed
                        ? {
                              width: '80vw',
                              maxWidth: '80vw',
                              borderRadius: 3,
                              border: '1px solid',
                              borderColor: 'divider',
                              maxHeight: '92vh',
                              overflow: 'hidden',
                              boxShadow:
                                  '0 24px 64px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.4)',
                          }
                        : {}),
                },
            }}
            sx={sx}
            aria-labelledby="draggable-dialog-title"
        >
            <Grid container sx={{ flexWrap: 'nowrap', flexDirection: 'column', minHeight: 0 }}>
                <Grid item xs={12}>
                    <Box
                        id={
                            draggable && windowed
                                ? `draggable-dialog-title`
                                : `non-draggable-dialog-title`
                        }
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: (t) => t.zIndex.appBar,
                            cursor: draggable && windowed ? 'move' : 'default',
                            // Slim, modern header: dark surface with a green
                            // brand accent underline instead of a heavy gradient.
                            bgcolor: 'background.paper',
                            borderBottom: '2px solid',
                            borderColor: 'primary.main',
                            backgroundImage:
                                'linear-gradient(104deg, rgba(43,40,40,1) 55%, rgba(60,79,44,0.55) 100%)',
                        }}
                    >
                        <Toolbar variant="dense" sx={{ minHeight: 56 }}>
                            <Typography
                                sx={{ flex: 1, fontWeight: 700 }}
                                variant="h6"
                                component="div"
                            >
                                {title}
                            </Typography>
                            {timeStamp && (
                                <Typography
                                    sx={{
                                        ml: 2,
                                        color: 'text.secondary',
                                        fontSize: 12,
                                    }}
                                    component="div"
                                >
                                    {timeStamp}
                                </Typography>
                            )}
                            {headerActions && (
                                <Box
                                    sx={{ ml: 2, display: 'flex', gap: 1 }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    {headerActions}
                                </Box>
                            )}
                            {minimizable && (
                                <IconButton
                                    edge="end"
                                    color="inherit"
                                    onClick={onMinimize}
                                    aria-label="minimize"
                                    sx={{ ml: 1 }}
                                >
                                    <MinimizeIcon />
                                </IconButton>
                            )}
                            <IconButton
                                edge="end"
                                onClick={onClose}
                                aria-label="close"
                                sx={{ color: 'text.secondary', ml: 1 }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Toolbar>
                    </Box>
                </Grid>

                <Grid
                    item
                    xs={12}
                    className="universal-modal-background"
                    sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}
                >
                    <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5, minWidth: 0 }}>
                        {children}
                    </Box>
                </Grid>

                {buttons.length > 0 && (
                    <Grid item xs={12}>
                        <DialogActions
                            sx={{
                                position: 'sticky',
                                bottom: 0,
                                borderTop: 1,
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                px: { xs: 2, md: 3 },
                                py: 1.5,
                                gap: 1,
                            }}
                        >
                            {buttons.map((button: any, i: number) => (
                                <Button
                                    key={button?.name ?? i}
                                    onClick={button.onClick}
                                    variant={
                                        i === buttons.length - 1
                                            ? 'contained'
                                            : 'text'
                                    }
                                >
                                    {button.name}
                                </Button>
                            ))}
                        </DialogActions>
                    </Grid>
                )}
            </Grid>
        </Dialog>
    )
}

export default UniversalModal
